// Grievance Log Dashboard — top KPI tiles + category bars + 6-month received trend.
import React from "react";
import { ReportShell, Card, SectionTitle, KPITile, MiniBars, fmtNum } from "./_shared";
import {
  CATEGORIES, categoryLabel, OPEN_STATUSES, daysToClose, isOverdue, notificationGap,
  average, countBy,
} from "../grievanceModel";

export default function GrievanceDashboard({ records = [] }) {
  const openCount = records.filter((r) => OPEN_STATUSES.includes(r.status)).length;
  const closedDays = records.map(daysToClose).filter((d) => d != null);
  const avgClose = average(closedDays);
  const overdue = records.filter((r) => isOverdue(r)).length;
  const gaps = records.filter(notificationGap).length;

  // grievances-by-category bars
  const catBars = countBy(records, (r) => r.category).map((c) => ({
    label: categoryLabel(c.key),
    value: c.count,
    color: CATEGORIES[c.key]?.color || "var(--ed-ink-3)",
  }));

  // 6-month received trend (last 6 calendar months including current)
  const now = new Date();
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleDateString("en-US", { month: "short" }), value: 0 });
  }
  const monthIndex = Object.fromEntries(months.map((m, i) => [m.key, i]));
  records.forEach((r) => {
    if (!r.dateReceived) return;
    const key = r.dateReceived.slice(0, 7);
    if (key in monthIndex) months[monthIndex[key]].value += 1;
  });

  return (
    <ReportShell title="Grievance Log Dashboard" subtitle="F585 readiness at a glance — open volume, timeliness, and the resident-notification gap.">
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
        <KPITile label="Open Grievances" value={openCount} tone={openCount === 0 ? "green" : openCount > 6 ? "red" : "yellow"} sub="open + in progress" />
        <KPITile label="Avg Days to Close" value={avgClose == null ? "—" : fmtNum(avgClose, 1)} tone={avgClose == null ? "neutral" : avgClose <= 5 ? "green" : avgClose <= 10 ? "yellow" : "red"} sub="resolved + closed" />
        <KPITile label="Overdue (>5 days)" value={overdue} tone={overdue === 0 ? "green" : overdue > 3 ? "red" : "yellow"} sub="still open past SLA" />
        <KPITile label="Notification Gaps" value={gaps} tone={gaps === 0 ? "green" : "red"} sub="resolved w/o resident notice" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 16 }}>
        <Card>
          <SectionTitle>Grievances by Category</SectionTitle>
          <MiniBars data={catBars} />
        </Card>
        <Card>
          <SectionTitle>Received — Last 6 Months</SectionTitle>
          <MiniBars data={months.map((m) => ({ label: m.label, value: m.value, color: "var(--ed-brand-lift)" }))} />
        </Card>
      </div>
    </ReportShell>
  );
}
